import { FLEET_GLYPH } from "@/shared/glyphs/fleets";
import { SHIP_GLYPH } from "@/shared/glyphs/ships";
import { routes as visualTestsRoutes } from "@/admin/pages/visual-tests/routes";
import { routes as modelsRoutes } from "@/admin/pages/models/routes";
import { routes as manufacturersRoutes } from "@/admin/pages/manufacturers/routes";
import { routes as blueprintsRoutes } from "@/admin/pages/blueprints/routes";
import { routes as componentsRoutes } from "@/admin/pages/components/routes";
import { routes as equipmentRoutes } from "@/admin/pages/equipment/routes";
import { routes as commoditiesRoutes } from "@/admin/pages/commodities/routes";
import { routes as missionsRoutes } from "@/admin/pages/missions/routes";
import { routes as locationsRoutes } from "@/admin/pages/locations/routes";
import { routes as fleetsRoutes } from "@/admin/pages/fleets/routes";
import { routes as vehiclesRoutes } from "@/admin/pages/vehicles/routes";
import { routes as adminsRoutes } from "@/admin/pages/admins/routes";
import { routes as oauthApplicationsRoutes } from "@/admin/pages/oauth-applications/routes";
import { routes as maintenanceRoutes } from "@/admin/pages/maintenance/routes";
import { routes as usersRoutes } from "@/admin/pages/users/routes";
import { routes as supporterContributionsRoutes } from "@/admin/pages/supporter-contributions/routes";
import { routes as announcementsRoutes } from "@/admin/pages/announcements/routes";
import { routes as gameEventsRoutes } from "@/admin/pages/game-events/routes";
import { routes as rafflesRoutes } from "@/admin/pages/raffles/routes";
import { RouteRecordRaw } from "vue-router";

/*
 * Vite mode, not NODE_ENV: a build sets NODE_ENV to "production" whatever the
 * mode, so a NODE_ENV gate hides these on stage too. Mode is Rails.env, so
 * development, stage and the e2e build keep them and only live strips them.
 */
const VisualTestsRoutes: RouteRecordRaw[] =
  import.meta.env.MODE !== "production"
    ? [
        {
          name: "admin-visual-tests",
          path: "/visual-tests/",
          component: () => import("@/admin/pages/visual-tests.vue"),
          children: visualTestsRoutes,
          redirect: { name: visualTestsRoutes[0].name },
          meta: {
            title: "admin.visualTests.index",
            icon: "fa-duotone fa-pen-swirl",
            // The nav renders its own entry, last and behind a divider.
            nav: "hidden",
            needsAuthentication: true,
          },
        },
      ]
    : [];

export const routes: RouteRecordRaw[] = [
  ...VisualTestsRoutes,
  {
    path: "/",
    name: "home",
    component: () => import("@/admin/pages/index.vue"),
    meta: {
      title: "home",
      icon: "fa-duotone fa-home-alt",
      needsAuthentication: true,
      exact: true,
      mobileNav: 0,
    },
  },
  {
    path: "/models/",
    component: () => import("@/admin/pages/models.vue"),
    children: modelsRoutes,
    redirect: { name: modelsRoutes[0].name },
    meta: {
      title: "admin.models.index",
      needsAuthentication: true,
      icon: SHIP_GLYPH,
      mobileNav: 1,
      access: ["models"],
    },
  },
  {
    path: "/blueprints/",
    component: () => import("@/admin/pages/blueprints.vue"),
    children: blueprintsRoutes,
    redirect: { name: blueprintsRoutes[0].name },
    meta: {
      title: "admin.blueprints.index",
      needsAuthentication: true,
      // The glyph the public catalogue nav gives blueprints, as components
      // and the group icon already match theirs.
      icon: "fa-duotone fa-notes",
      access: ["blueprints"],
    },
  },
  {
    path: "/components/",
    component: () => import("@/admin/pages/components.vue"),
    children: componentsRoutes,
    redirect: { name: componentsRoutes[0].name },
    meta: {
      title: "admin.components.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-microchip",
      access: ["components"],
    },
  },
  {
    path: "/equipment/",
    component: () => import("@/admin/pages/equipment.vue"),
    children: equipmentRoutes,
    redirect: { name: equipmentRoutes[0].name },
    meta: {
      title: "admin.equipment.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-shirt",
      access: ["equipment"],
    },
  },
  {
    path: "/commodities/",
    component: () => import("@/admin/pages/commodities.vue"),
    children: commoditiesRoutes,
    redirect: { name: commoditiesRoutes[0].name },
    meta: {
      title: "admin.commodities.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-boxes-stacked",
      access: ["commodities"],
    },
  },
  {
    path: "/missions/",
    component: () => import("@/admin/pages/missions.vue"),
    children: missionsRoutes,
    redirect: { name: missionsRoutes[0].name },
    meta: {
      title: "admin.missions.index",
      needsAuthentication: true,
      // The glyph the public catalogue nav gives missions.
      icon: "fa-duotone fa-scroll",
      access: ["missions"],
    },
  },
  {
    path: "/locations/",
    component: () => import("@/admin/pages/locations.vue"),
    children: locationsRoutes,
    redirect: { name: locationsRoutes[0].name },
    meta: {
      title: "admin.locations.index",
      needsAuthentication: true,
      // The glyph the public nav gives locations.
      icon: "fa-duotone fa-planet-ringed",
      access: ["locations"],
    },
  },
  {
    path: "/manufacturers/",
    component: () => import("@/admin/pages/manufacturers.vue"),
    children: manufacturersRoutes,
    redirect: { name: manufacturersRoutes[0].name },
    meta: {
      title: "admin.manufacturers.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-buildings",
      access: ["manufacturers"],
    },
  },
  {
    path: "/images",
    name: "admin-images",
    component: () => import("@/admin/pages/images.vue"),
    meta: {
      title: "admin.images.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-images",
      access: ["images"],
    },
  },
  {
    path: "/vehicles/",
    component: () => import("@/admin/pages/vehicles.vue"),
    children: vehiclesRoutes,
    redirect: { name: vehiclesRoutes[0].name },
    meta: {
      title: "admin.vehicles.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-warehouse",
      access: ["vehicles"],
    },
  },
  {
    path: "/fleets/",
    component: () => import("@/admin/pages/fleets.vue"),
    children: fleetsRoutes,
    redirect: { name: fleetsRoutes[0].name },
    meta: {
      title: "admin.fleets.index",
      needsAuthentication: true,
      icon: FLEET_GLYPH,
      access: ["fleets"],
    },
  },
  {
    path: "/users",
    component: () => import("@/admin/pages/users.vue"),
    children: usersRoutes,
    redirect: { name: usersRoutes[0].name },
    meta: {
      title: "admin.users.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-users",
      access: ["users"],
    },
  },
  {
    path: "/admins/",
    component: () => import("@/admin/pages/admins.vue"),
    children: adminsRoutes,
    redirect: { name: adminsRoutes[0].name },
    meta: {
      title: "admin.admins.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-user-group-crown",
      access: ["admins"],
    },
  },
  {
    path: "/supporter-contributions/",
    component: () => import("@/admin/pages/supporter-contributions.vue"),
    children: supporterContributionsRoutes,
    redirect: { name: supporterContributionsRoutes[0].name },
    meta: {
      title: "admin.supporterContributions.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-hand-holding-heart",
      mobileNav: 2,
      access: ["supporters"],
    },
  },
  {
    path: "/announcements/",
    component: () => import("@/admin/pages/announcements.vue"),
    children: announcementsRoutes,
    redirect: { name: announcementsRoutes[0].name },
    meta: {
      title: "admin.announcements.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-bullhorn",
      // Linked from the notifications page rather than carried in the nav: the
      // two are the same job seen from either end -- what the admin sends out,
      // and what comes back in. So Notifications is the row that lights up
      // here, or the nav answers nothing at all on an announcements page.
      nav: "hidden",
      activeRoute: "admin-notifications",
      access: ["announcements"],
    },
  },
  {
    path: "/homepage/",
    name: "admin-homepage",
    component: () => import("@/admin/pages/homepage.vue"),
    meta: {
      title: "admin.homepage",
      needsAuthentication: true,
      icon: "fa-duotone fa-house",
      access: ["homepage"],
    },
  },
  {
    path: "/game-events/",
    component: () => import("@/admin/pages/game-events.vue"),
    children: gameEventsRoutes,
    redirect: { name: gameEventsRoutes[0].name },
    meta: {
      title: "admin.gameEvents.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-calendar-star",
      access: ["game_events"],
    },
  },
  {
    path: "/raffles/",
    component: () => import("@/admin/pages/raffles.vue"),
    children: rafflesRoutes,
    redirect: { name: rafflesRoutes[0].name },
    meta: {
      title: "admin.raffles.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-ticket",
      access: ["raffles"],
    },
  },
  {
    path: "/oauth-applications/",
    component: () => import("@/admin/pages/oauth-applications.vue"),
    children: oauthApplicationsRoutes,
    redirect: { name: oauthApplicationsRoutes[0].name },
    meta: {
      title: "admin.oauthApplications.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-key",
      access: ["oauth_applications"],
    },
  },
  ...maintenanceRoutes,
  {
    path: "/notifications",
    name: "admin-notifications",
    component: () => import("@/admin/pages/notifications.vue"),
    meta: {
      title: "admin.notifications.index",
      needsAuthentication: true,
      icon: "fa-duotone fa-bell",
      nav: "hidden",
      mobileBack: true,
    },
  },
  {
    path: "/login/",
    name: "admin-login",
    component: () => import("@/admin/pages/login.vue"),
    meta: {
      title: "login",
      icon: "fa-light fa-sign-in",
      hideWhenAuthenticated: true,
      nav: "footer",
    },
  },
  {
    path: "/404/",
    name: "404",
    component: () => import("@/admin/pages/404.vue"),
    meta: {
      title: "notFound",
      backgroundImage: "bg-404",
      nav: "hidden",
    },
  },
  {
    path: "/:pathMatch(.*)*",
    component: () => import("@/admin/pages/404.vue"),
    meta: {
      title: "notFound",
      backgroundImage: "bg-404",
      nav: "hidden",
    },
  },
];
